import { useEffect, useRef, useState } from "react";
import { useLocales } from "../../hooks/LocalesContext";
import { MenuItem, Select, SelectChangeEvent, FormControl, InputLabel, Box, Typography, SxProps, Theme } from "@mui/material";

interface LocalSelectorProps {
    onLocalChange: (idLocal: string | undefined) => void;
    layout?: 'horizontal' | 'vertical'; // Optional layout parameter
    label?: string; // Optional custom label
    size?: 'small' | 'medium';
    sx?: SxProps<Theme>;
    selectSx?: SxProps<Theme>;
}

const LocalSelector: React.FC<LocalSelectorProps> = ({ 
    onLocalChange, 
    layout = 'vertical', 
    label = 'Local',
    size = 'medium',
    sx,
    selectSx
}) => {
    const { locales } = useLocales();
    const [selectedLocal, setSelectedLocal] = useState<string>("");
    const onLocalChangeRef = useRef(onLocalChange);
    const lastNotifiedRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        onLocalChangeRef.current = onLocalChange;
    }, [onLocalChange]);

    useEffect(() => {
        const notify = (value: string | undefined) => {
            if (lastNotifiedRef.current !== value) {
                lastNotifiedRef.current = value;
                onLocalChangeRef.current(value);
            }
        };

        if (locales.length > 0) {
            const currentExists = locales.some((local) => local.idLocal === selectedLocal);
            const nextLocal = currentExists ? selectedLocal : locales[0].idLocal;
            if (nextLocal !== selectedLocal) {
                setSelectedLocal(nextLocal);
            }
            notify(nextLocal);
        } else {
            setSelectedLocal("");
            notify(undefined);
        }
    }, [locales, selectedLocal]);

    const handleChange = (event: SelectChangeEvent) => {
        lastNotifiedRef.current = event.target.value;
        onLocalChange(event.target.value);
        setSelectedLocal(event.target.value);
    };

    return (
        <>
            {locales && (
                layout === 'vertical' ? (
                    <FormControl fullWidth size={size} sx={sx}>
                        <InputLabel id="local-select-label">{label}</InputLabel>
                        <Select
                            labelId="local-select-label"
                            id="local-select"
                            value={selectedLocal}
                            label={label}
                            onChange={handleChange}
                            sx={selectSx}
                        >
                            {locales.map(l => (
                                <MenuItem key={l.idLocal} value={l.idLocal}>{l.fantasia}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography>{label}:</Typography>
                        <Select
                            labelId="local-select-label-horizontal"
                            id="local-select-horizontal"
                            value={selectedLocal}
                            onChange={handleChange}
                            variant="outlined"
                            size={size}
                            sx={selectSx}
                        >
                            {locales.map(l => (
                                <MenuItem key={l.idLocal} value={l.idLocal}>{l.fantasia}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                )
            )}
        </>
    );
}

export default LocalSelector;
